import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalBadge } from "@/components/ui/BrutalBadge";
import { SimpleRichTextEditor } from "@/components/editor/SimpleRichTextEditor";
import { JobFormData } from "@/pages/dashboard/JobCreate";
import { Plus, X, Globe } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { sanitizeText } from "@/lib/sanitize";

interface JobFormStep2Props {
  formData: JobFormData;
  updateFormData: (updates: Partial<JobFormData>) => void;
}

const languageTabs = [
  { key: 'it', label: 'Italiano', flag: '🇮🇹', required: true },
  { key: 'en', label: 'English', flag: '🇬🇧', required: false },
  { key: 'es', label: 'Español', flag: '🇪🇸', required: false },
] as const;

type LanguageKey = typeof languageTabs[number]['key'];

export function JobFormStep2({ formData, updateFormData }: JobFormStep2Props) {
  const { t } = useTranslation();
  const [newRequiredSkill, setNewRequiredSkill] = useState('');
  const [newNiceSkill, setNewNiceSkill] = useState('');
  const [activeTab, setActiveTab] = useState<LanguageKey>('it');

  const addRequiredSkill = () => {
    const cleanedSkill = sanitizeText(newRequiredSkill.trim());
    if (cleanedSkill && !formData.required_skills.includes(cleanedSkill)) {
      updateFormData({ required_skills: [...formData.required_skills, cleanedSkill] });
      setNewRequiredSkill('');
    }
  };

  const removeRequiredSkill = (skill: string) => {
    updateFormData({ required_skills: formData.required_skills.filter(s => s !== skill) });
  };

  const addNiceSkill = () => {
    const cleanedSkill = sanitizeText(newNiceSkill.trim());
    if (cleanedSkill && !formData.nice_to_have_skills.includes(cleanedSkill)) {
      updateFormData({ nice_to_have_skills: [...formData.nice_to_have_skills, cleanedSkill] });
      setNewNiceSkill('');
    }
  };

  const removeNiceSkill = (skill: string) => {
    updateFormData({ nice_to_have_skills: formData.nice_to_have_skills.filter(s => s !== skill) });
  };

  const getDescriptionValue = (lang: LanguageKey): string => {
    switch (lang) {
      case 'it': return formData.description_it || '';
      case 'en': return formData.description_en || '';
      case 'es': return formData.description_es || '';
      default: return '';
    }
  };

  const updateDescription = (lang: LanguageKey, value: string) => {
    switch (lang) {
      case 'it':
        // Also update legacy description field for backward compatibility
        updateFormData({ description_it: value, description: value });
        break;
      case 'en':
        updateFormData({ description_en: value });
        break;
      case 'es':
        updateFormData({ description_es: value });
        break;
    }
  };

  const getDescriptionLength = (lang: LanguageKey): number => {
    return getDescriptionValue(lang).length;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-xl font-bold text-dark mb-2">
          {t("jobs.form.step2Title")}
        </h2>
        <p className="text-muted-foreground">
          {t("jobs.form.step2Desc")}
        </p>
      </div>

      {/* Multilingual Description with Tabs */}
      <div>
        <label className="flex items-center gap-2 font-semibold text-dark mb-3">
          <Globe className="w-4 h-4" />
          {t("jobs.form.description")} *
        </label>
        <p className="text-sm text-muted-foreground mb-3">
          {t("jobs.form.descriptionMultilingualHint")}
        </p>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as LanguageKey)}>
          <TabsList className="mb-4 border-[2px] border-dark bg-background">
            {languageTabs.map((lang) => (
              <TabsTrigger 
                key={lang.key} 
                value={lang.key}
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground gap-2"
              >
                <span>{lang.flag}</span>
                <span className="hidden sm:inline">{lang.label}</span>
                {lang.required && <span className="text-destructive">*</span>}
                {getDescriptionLength(lang.key) > 0 && (
                  <span className="text-xs bg-success/20 text-success px-1.5 py-0.5 rounded-full">
                    ✓
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {languageTabs.map((lang) => (
            <TabsContent key={lang.key} value={lang.key} className="mt-0">
              <SimpleRichTextEditor
                value={getDescriptionValue(lang.key)}
                onChange={(html) => updateDescription(lang.key, html)}
                placeholder={t(`jobs.form.descriptionPlaceholder${lang.key.toUpperCase()}`, t("jobs.form.descriptionPlaceholder"))}
              />
              <div className="flex justify-between mt-2">
                <p className="text-xs text-muted-foreground">
                  {lang.required 
                    ? t("jobs.form.descriptionHintRequired")
                    : t("jobs.form.descriptionHintOptional")
                  }
                </p>
                <span className={`text-xs ${
                  lang.required && getDescriptionLength(lang.key) < 100 
                    ? 'text-muted-foreground' 
                    : getDescriptionLength(lang.key) >= 100 
                      ? 'text-success' 
                      : 'text-muted-foreground'
                }`}>
                  {getDescriptionLength(lang.key)}{lang.required ? '/100' : ''}
                </span>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Required Skills */}
      <div>
        <label className="block font-semibold text-dark mb-2">
          {t("jobs.form.requiredSkills")}
        </label>
        <div className="flex gap-2 mb-3">
          <BrutalInput
            value={newRequiredSkill}
            onChange={(e) => setNewRequiredSkill(e.target.value)}
            placeholder={t("jobs.form.addSkill")}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRequiredSkill())}
          />
          <button
            type="button"
            onClick={addRequiredSkill}
            className="px-4 py-2 bg-primary border-[3px] border-dark hover:bg-primary/80 transition-colors"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
        {formData.required_skills.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {formData.required_skills.map((skill) => (
              <BrutalBadge key={skill} variant="default" className="pr-1">
                {skill}
                <button
                  type="button"
                  onClick={() => removeRequiredSkill(skill)}
                  className="ml-2 hover:text-destructive"
                >
                  <X className="w-3 h-3" />
                </button>
              </BrutalBadge>
            ))}
          </div>
        )}
      </div>

      {/* Nice to Have Skills */}
      <div>
        <label className="block font-semibold text-dark mb-2">
          {t("jobs.form.niceToHave")} ({t("common.optional")})
        </label>
        <div className="flex gap-2 mb-3">
          <BrutalInput
            value={newNiceSkill}
            onChange={(e) => setNewNiceSkill(e.target.value)}
            placeholder={t("jobs.form.addSkill")}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addNiceSkill())}
          />
          <button
            type="button"
            onClick={addNiceSkill}
            className="px-4 py-2 bg-secondary border-[3px] border-dark hover:bg-secondary/80 transition-colors"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
        {formData.nice_to_have_skills.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {formData.nice_to_have_skills.map((skill) => (
              <BrutalBadge key={skill} variant="secondary" className="pr-1">
                {skill}
                <button
                  type="button"
                  onClick={() => removeNiceSkill(skill)}
                  className="ml-2 hover:text-destructive"
                >
                  <X className="w-3 h-3" />
                </button>
              </BrutalBadge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
